import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ShieldAlert } from 'lucide-react';
import { matchingAPI } from '../../lib/api';

export function StaleSuggestedMatchesSweepPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await matchingAPI.previewStaleSuggestedMatchesSweep();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Supersede ${preview?.stale_count || 0} suggested match(es) that fail the current ` +
      'matching gates (date scope, settled invoice, amount agreement)? Reversible -- moves ' +
      'them to status=superseded_by_rerun, never deletes.'
    )) return;
    setApplyLoading(true);
    try {
      const res = await matchingAPI.applyStaleSuggestedMatchesSweep();
      setApplyResult(res.data);
    } catch {
      setApplyResult({ error: 'Failed to apply sweep.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Stale Suggested Matches Sweep</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Supersedes suggested matches that fail the current matching gates (2026-01-01+ date
          scope, settled-invoice exclusion, amount-agreement for Medium/High) but were persisted
          before those gates existed, so the Suggested Matches counter reflects the current
          rules. Two-step: Preview, then Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="stale-matches-sweep-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.stale_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="stale-matches-sweep-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply to {preview.stale_count} Matches
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="stale-matches-sweep-preview-result">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Scanned: <strong>{preview.suggested_scanned}</strong></span>
                  <span>Stale: <strong>{preview.stale_count}</strong></span>
                  {preview.truncated && (
                    <span className="text-amber-600">
                      Truncated at scan cap ({preview.suggested_total} total suggested)
                    </span>
                  )}
                </div>
                {preview.reason_counts && Object.keys(preview.reason_counts).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.reason_counts).map(([reason, count]) => (
                      <Badge key={reason} variant="outline" className="text-[10px]">
                        {reason}: {count}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="stale-matches-sweep-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Superseded:</strong> {applyResult.swept} of {applyResult.stale_count}</p>
                <p className="text-muted-foreground">{applyResult.message}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
